// src/scenes/SignInSceneDOM.js - Full-page sign-in experience matching templates exactly
import BaseMenuScene from "./BaseMenuScene.js";
import { MobileDetectionService } from "../services/MobileDetectionService.js";

export default class SignInSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "SignInSceneDOM" });
        this.overlayId = "signin-scene-overlay";
    }

    create() {
        console.log("SignInSceneDOM: Creating full-page sign-in experience");
        super.create();
        
        // Hide game container and show full-page sign-in
        this.hideGameContainer();
        this.setRibbonVisibility(false); // Hide ribbon for clean sign-in experience
        
        // Create full-page sign-in interface
        this.createSignInInterface();
    }

    hideGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            this.originalGameContainerStyles = {
                display: gameContainer.style.display || 'block',
                visibility: gameContainer.style.visibility || 'visible'
            };
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
    }

    showGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer && this.originalGameContainerStyles) {
            gameContainer.style.display = this.originalGameContainerStyles.display;
            gameContainer.style.visibility = this.originalGameContainerStyles.visibility;
        }
    }

    createSignInInterface() {
        // Remove existing overlay
        this.removeExistingOverlay();

        // Detect mobile and use appropriate template
        const isMobile = MobileDetectionService.isMobile();
        console.log(`SignInSceneDOM: Device detection - Mobile: ${isMobile}`);
        
        if (isMobile) {
            this.createMobileSignInInterface();
        } else {
            this.createDesktopSignInInterface();
        }
    }

    createDesktopSignInInterface() {
        // Create main overlay using desktop template design
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: #0f172a;
            color: #ffffff;
            z-index: 10000;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
        `;

        overlay.innerHTML = `
            <!-- Premium Background - Exact from Desktop Template -->
            <div class="background" style="
                position: fixed;
                inset: 0;
                background: 
                    radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
                    radial-gradient(circle at 80% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
                    radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%);
                z-index: -2;
            "></div>
            
            <div class="background-grid" style="
                position: fixed;
                inset: 0;
                background-image: 
                    linear-gradient(rgba(0, 212, 255, 0.01) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(0, 212, 255, 0.01) 1px, transparent 1px);
                background-size: 50px 50px;
                z-index: -1;
                animation: gridMove 30s linear infinite;
            "></div>

            <!-- Sign In Card - Desktop Template -->
            <div class="signin-card" style="
                width: 100%;
                max-width: 480px;
                background: rgba(30, 41, 59, 0.5);
                backdrop-filter: blur(40px);
                -webkit-backdrop-filter: blur(40px);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 24px;
                padding: 48px;
                box-shadow: 
                    0 20px 50px rgba(0, 0, 0, 0.5),
                    0 0 100px rgba(0, 212, 255, 0.1),
                    inset 0 1px 1px rgba(255, 255, 255, 0.05);
                position: relative;
                overflow: hidden;
                animation: cardEntry 0.8s cubic-bezier(0.21, 1.02, 0.73, 1);
            ">
                <!-- Logo Section -->
                <div class="signin-logo-section" style="
                    text-align: center;
                    margin-bottom: 40px;
                ">
                    <div class="signin-logo-container" style="
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 16px;
                        margin-bottom: 24px;
                    ">
                        <div class="signin-logo" style="
                            width: 48px;
                            height: 48px;
                            background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                            border-radius: 12px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 24px;
                            font-weight: 800;
                            color: white;
                            box-shadow: 0 8px 32px rgba(0, 212, 255, 0.3);
                        ">D</div>
                        <div class="signin-brand-text">
                            <div class="signin-brand-name" style="
                                font-size: 28px;
                                font-weight: 800;
                                color: #ffffff;
                                line-height: 1;
                            ">DeckOps.ai</div>
                            <div class="signin-brand-tagline" style="
                                font-size: 12px;
                                color: rgba(255, 255, 255, 0.6);
                                letter-spacing: 2px;
                                text-transform: uppercase;
                                margin-top: 4px;
                            ">EXECUTIVE PLATFORM</div>
                        </div>
                    </div>
                    
                    <h1 style="
                        font-size: 32px;
                        font-weight: 700;
                        color: #ffffff;
                        margin-bottom: 8px;
                        line-height: 1.2;
                    ">Welcome Back</h1>
                    <p style="
                        font-size: 16px;
                        color: rgba(255, 255, 255, 0.7);
                        line-height: 1.5;
                    ">Sign in to access your executive training dashboard</p>
                </div>

                <!-- Sign In Form -->
                <form class="signin-form" id="signin-form" onsubmit="return false;" style="
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                ">
                    <!-- Email Field -->
                    <div class="signin-input-wrapper" style="position: relative;">
                        <div class="signin-input-icon" style="
                            position: absolute;
                            left: 16px;
                            top: 50%;
                            transform: translateY(-50%);
                            width: 18px;
                            height: 18px;
                            color: rgba(255, 255, 255, 0.5);
                        ">✉</div>
                        <input 
                            type="email" 
                            id="signin-email" 
                            class="signin-form-input" 
                            placeholder="Enter your email address"
                            required
                            style="
                                width: 100%;
                                height: 48px;
                                padding: 0 16px 0 44px;
                                background: rgba(255, 255, 255, 0.05);
                                border: 1px solid rgba(255, 255, 255, 0.08);
                                border-radius: 12px;
                                color: #ffffff;
                                font-size: 15px;
                                font-weight: 400;
                                transition: all 0.15s ease;
                            "
                        >
                    </div>

                    <!-- Password Field -->
                    <div class="signin-input-wrapper" style="position: relative;">
                        <div class="signin-input-icon" style="
                            position: absolute;
                            left: 16px;
                            top: 50%;
                            transform: translateY(-50%);
                            width: 18px;
                            height: 18px;
                            color: rgba(255, 255, 255, 0.5);
                        ">🔒</div>
                        <input 
                            type="password" 
                            id="signin-password" 
                            class="signin-form-input" 
                            placeholder="Enter your password"
                            required
                            style="
                                width: 100%;
                                height: 48px;
                                padding: 0 16px 0 44px;
                                background: rgba(255, 255, 255, 0.05);
                                border: 1px solid rgba(255, 255, 255, 0.08);
                                border-radius: 12px;
                                color: #ffffff;
                                font-size: 15px;
                                font-weight: 400;
                                transition: all 0.15s ease;
                            "
                        >
                    </div>

                    <!-- Sign In Button -->
                    <button type="button" id="signin-submit-btn" class="signin-submit-btn" style="
                        width: 100%;
                        height: 48px;
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border: none;
                        border-radius: 12px;
                        color: #ffffff;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        box-shadow: 0 4px 16px rgba(0, 212, 255, 0.3);
                    ">Sign In</button>

                    <!-- Switch to Sign Up -->
                    <div style="
                        text-align: center;
                        margin-top: 24px;
                    ">
                        <span style="
                            color: rgba(255, 255, 255, 0.7);
                            font-size: 14px;
                        ">Don't have an account? </span>
                        <a href="#" id="switch-to-signup" style="
                            color: #00d4ff;
                            text-decoration: none;
                            font-size: 14px;
                            font-weight: 600;
                            transition: color 0.15s ease;
                        ">Sign up for free</a>
                    </div>

                    <!-- Back to Home -->
                    <div style="
                        text-align: center;
                        margin-top: 16px;
                    ">
                        <a href="#" id="back-to-home" style="
                            color: rgba(255, 255, 255, 0.5);
                            text-decoration: none;
                            font-size: 14px;
                            transition: color 0.15s ease;
                        ">← Back to home</a>
                    </div>
                </form>
            </div>

            <!-- CSS Animations -->
            <style>
                @keyframes gridMove {
                    0% { transform: translate(0, 0); }
                    100% { transform: translate(50px, 50px); }
                }
                
                @keyframes cardEntry {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                .signin-form-input:hover {
                    background: rgba(255, 255, 255, 0.06) !important;
                }
                
                .signin-form-input:focus {
                    outline: none;
                    background: rgba(255, 255, 255, 0.08) !important;
                    border-color: #00d4ff !important;
                    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1) !important;
                }
                
                .signin-form-input::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }

                .signin-submit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0, 212, 255, 0.4) !important;
                }

                #switch-to-signup:hover,
                #back-to-home:hover {
                    color: #0ea5e9 !important;
                }
            </style>
        `;

        // Add to body
        document.body.appendChild(overlay);

        // Bind events
        this.bindSignInEvents();

        // Focus email input
        setTimeout(() => {
            const emailInput = document.getElementById('signin-email');
            if (emailInput) emailInput.focus();
        }, 100);
    }

    createMobileSignInInterface() {
        // Create mobile-optimized overlay using mobile template design
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: #ffffff;
            z-index: 10000;
            overflow-y: auto;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif;
        `;

        overlay.innerHTML = `
            <!-- Mobile Container -->
            <div class="container" style="
                position: relative;
                z-index: 1;
                min-height: 100vh;
                min-height: 100dvh;
                display: flex;
                flex-direction: column;
                padding: 24px;
                padding-top: calc(48px + env(safe-area-inset-top));
                padding-bottom: calc(24px + env(safe-area-inset-bottom));
            ">
                <!-- Header -->
                <div class="header" style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 32px;
                ">
                    <button id="mobile-back-to-home" style="
                        background: none;
                        border: none;
                        color: rgba(255, 255, 255, 0.7);
                        font-size: 16px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px;
                        border-radius: 8px;
                        transition: all 0.15s ease;
                    ">← Back</button>
                    <div style="
                        font-size: 20px;
                        font-weight: 700;
                        color: #00d4ff;
                    ">DeckOps.ai</div>
                    <div style="width: 60px;"></div>
                </div>

                <!-- Logo Section -->
                <div class="logo-section" style="
                    text-align: center;
                    margin-bottom: 64px;
                ">
                    <div class="logo" style="
                        width: 64px;
                        height: 64px;
                        margin: 0 auto 24px;
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        border-radius: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 32px;
                        font-weight: 800;
                        color: #0f172a;
                        box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
                    ">D</div>
                    
                    <div class="brand-name" style="
                        font-size: 26px;
                        font-weight: 700;
                        letter-spacing: -0.5px;
                        margin-bottom: 8px;
                        background: linear-gradient(135deg, #ffffff 0%, #00d4ff 100%);
                        -webkit-background-clip: text;
                        background-clip: text;
                        -webkit-text-fill-color: transparent;
                    ">DeckOps.ai</div>
                    <div class="brand-tagline" style="
                        font-size: 13px;
                        color: rgba(148, 163, 184, 1);
                        letter-spacing: 0.5px;
                        font-weight: 500;
                    ">EXECUTIVE PLATFORM</div>
                </div>

                <!-- Form Container -->
                <div class="form-container" style="
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    max-width: 400px;
                    width: 100%;
                    margin: 0 auto;
                ">
                    <div class="form-header" style="
                        text-align: center;
                        margin-bottom: 32px;
                    ">
                        <h1 style="
                            font-size: 28px;
                            font-weight: 600;
                            letter-spacing: -0.5px;
                            margin-bottom: 8px;
                            color: #ffffff;
                        ">Welcome Back</h1>
                        <p style="
                            font-size: 15px;
                            color: rgba(255, 255, 255, 0.7);
                            line-height: 1.4;
                        ">Sign in to your executive account</p>
                    </div>

                    <!-- Sign In Form -->
                    <form class="signin-form" id="mobile-signin-form" style="
                        display: flex;
                        flex-direction: column;
                        gap: 24px;
                    ">
                        <!-- Email Field -->
                        <div class="form-group">
                            <label style="
                                display: block;
                                font-size: 12px;
                                font-weight: 600;
                                color: rgba(255, 255, 255, 0.8);
                                text-transform: uppercase;
                                letter-spacing: 0.8px;
                                margin-bottom: 8px;
                            ">Email Address</label>
                            <input 
                                type="email" 
                                id="mobile-signin-email" 
                                placeholder="Enter your email"
                                required
                                style="
                                    width: 100%;
                                    height: 52px;
                                    padding: 0 16px;
                                    background: rgba(30, 41, 59, 0.4);
                                    border: 1px solid rgba(255, 255, 255, 0.08);
                                    border-radius: 12px;
                                    color: #ffffff;
                                    font-size: 16px;
                                    transition: all 0.15s ease;
                                "
                            >
                        </div>

                        <!-- Password Field -->
                        <div class="form-group">
                            <label style="
                                display: block;
                                font-size: 12px;
                                font-weight: 600;
                                color: rgba(255, 255, 255, 0.8);
                                text-transform: uppercase;
                                letter-spacing: 0.8px;
                                margin-bottom: 8px;
                            ">Password</label>
                            <input 
                                type="password" 
                                id="mobile-signin-password" 
                                placeholder="Enter your password"
                                required
                                style="
                                    width: 100%;
                                    height: 52px;
                                    padding: 0 16px;
                                    background: rgba(30, 41, 59, 0.4);
                                    border: 1px solid rgba(255, 255, 255, 0.08);
                                    border-radius: 12px;
                                    color: #ffffff;
                                    font-size: 16px;
                                    transition: all 0.15s ease;
                                "
                            >
                        </div>

                        <!-- Sign In Button -->
                        <button type="submit" class="signin-submit-btn" style="
                            width: 100%;
                            height: 52px;
                            background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                            border: none;
                            border-radius: 12px;
                            color: #ffffff;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: all 0.15s ease;
                            box-shadow: 0 4px 16px rgba(0, 212, 255, 0.3);
                            margin-top: 8px;
                        ">Sign In</button>

                        <!-- Sign Up Link -->
                        <div style="
                            text-align: center;
                            margin-top: 24px;
                        ">
                            <span style="
                                color: rgba(255, 255, 255, 0.7);
                                font-size: 14px;
                            ">Don't have an account? </span>
                            <a href="#" id="mobile-switch-to-signup" style="
                                color: #00d4ff;
                                text-decoration: none;
                                font-size: 14px;
                                font-weight: 600;
                                transition: color 0.15s ease;
                            ">Sign up for free</a>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Mobile CSS -->
            <style>
                input:focus {
                    outline: none;
                    background: rgba(30, 41, 59, 0.6) !important;
                    border-color: #00d4ff !important;
                    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1) !important;
                }
                
                input::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }

                .signin-submit-btn:hover, .signin-submit-btn:active {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4) !important;
                }

                #mobile-back-to-home:hover {
                    background: rgba(255, 255, 255, 0.05) !important;
                    color: #ffffff !important;
                }

                #mobile-switch-to-signup:hover {
                    color: #0ea5e9 !important;
                }
            </style>
        `;

        // Add to body
        document.body.appendChild(overlay);

        // Bind mobile events
        this.bindMobileSignInEvents();

        // Focus email input
        setTimeout(() => {
            const emailInput = document.getElementById('mobile-signin-email');
            if (emailInput) emailInput.focus();
        }, 100);
    }

    bindSignInEvents() {
        // Button click instead of form submission to prevent page reload
        const submitBtn = document.getElementById('signin-submit-btn');
        if (submitBtn) {
            console.log('🔗 Desktop: Binding click event to signin-submit-btn');
            submitBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔥 Desktop: Sign in button clicked!');
                this.handleSignIn();
            });
        } else {
            console.error('❌ Desktop: signin-submit-btn element not found for event binding');
        }

        // Back to home
        const backToHome = document.getElementById('back-to-home');
        if (backToHome) {
            backToHome.addEventListener('click', (e) => {
                e.preventDefault();
                this.goBackToHome();
            });
        }

        // Switch to sign up
        const switchToSignup = document.getElementById('switch-to-signup');
        if (switchToSignup) {
            switchToSignup.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchToSignUp();
            });
        }
    }

    bindMobileSignInEvents() {
        // Form submission
        const form = document.getElementById('mobile-signin-form');
        if (form) {
            console.log('🔗 Mobile: Binding form submission event to mobile-signin-form');
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                console.log('🔥 Mobile: Form submission triggered!');
                this.handleMobileSignIn();
            });
        } else {
            console.error('❌ Mobile: mobile-signin-form element not found for event binding');
        }

        // Back to home
        const backToHome = document.getElementById('mobile-back-to-home');
        if (backToHome) {
            backToHome.addEventListener('click', (e) => {
                e.preventDefault();
                this.goBackToHome();
            });
        }

        // Switch to sign up
        const switchToSignup = document.getElementById('mobile-switch-to-signup');
        if (switchToSignup) {
            switchToSignup.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchToSignUp();
            });
        }
    }

    async handleSignIn() {
        const email = document.getElementById('signin-email').value;
        const password = document.getElementById('signin-password').value;

        if (!email || !password) {
            this.showMessage('Please fill in all fields', 3000, true);
            return;
        }

        // Show loading indicator
        const submitBtn = document.getElementById('signin-submit-btn');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Signing in...';
        submitBtn.disabled = true;

        try {
            // CRITICAL: Use the exact same Firebase auth approach as working system
            const { auth } = await import('../firebaseConfig.js');
            console.log('🔐 Desktop: Attempting Firebase sign in with:', email);
            console.log('🔐 Desktop: Firebase auth object:', auth);
            console.log('🔐 Desktop: Firebase app instance:', auth.app);
            console.log('🔐 Desktop: Current user before sign-in:', auth.currentUser);
            
            // Add timeout to prevent hanging
            const signInPromise = auth.signInWithEmailAndPassword(email, password);
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Firebase sign-in timeout after 10 seconds')), 10000);
            });
            
            console.log('🔐 Desktop: Starting Firebase authentication with timeout...');
            const userCredential = await Promise.race([signInPromise, timeoutPromise]);
            
            console.log('✅ Desktop: User signed in successfully:', userCredential.user.email);
            console.log('✅ Desktop: User emailVerified:', userCredential.user.emailVerified);
            
            // CRITICAL: Give Firebase a moment to persist the auth state before removing overlay
            setTimeout(() => {
                this.removeExistingOverlay();
                console.log('✅ Desktop sign in successful, overlay removed after Firebase persistence');
            }, 500);
        } catch (error) {
            console.error('🚨 Desktop sign in error:', error);
            if (error.message.includes('timeout')) {
                this.showMessage('Sign in is taking too long. Please check your internet connection and try again.', 5000, true);
            } else {
                this.showMessage('Sign in failed. Please check your credentials.', 3000, true);
            }
        } finally {
            // Reset button state
            if (submitBtn) {
                submitBtn.textContent = originalText;
                submitBtn.disabled = false;
            }
        }
    }

    async handleMobileSignIn() {
        const email = document.getElementById('mobile-signin-email').value;
        const password = document.getElementById('mobile-signin-password').value;

        if (!email || !password) {
            this.showMessage('Please fill in all fields', 3000, true);
            return;
        }

        try {
            // CRITICAL: Use the exact same Firebase auth approach as working system
            const { auth } = await import('../firebaseConfig.js');
            console.log('🔐 Mobile: Attempting Firebase sign in with:', email);
            
            const userCredential = await auth.signInWithEmailAndPassword(email, password);
            console.log('✅ Mobile: User signed in successfully:', userCredential.user.email);
            console.log('✅ Mobile: User emailVerified:', userCredential.user.emailVerified);
            
            // CRITICAL: Give Firebase a moment to persist the auth state before removing overlay
            setTimeout(() => {
                this.removeExistingOverlay();
                console.log('✅ Mobile sign in successful, overlay removed after Firebase persistence');
            }, 500);
        } catch (error) {
            console.error('Mobile sign in error:', error);
            this.showMessage('Sign in failed. Please check your credentials.', 3000, true);
        }
    }

    switchToSignUp() {
        // Use proper scene transition following project architecture
        this.startScene('SignUpSceneDOM');
    }

    goBackToHome() {
        // Use proper scene transition following project architecture
        this.startScene('GuestMenuSceneDOM');
    }

    showMessage(message, duration = 3000, isError = false) {
        // Create temporary message overlay
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${isError ? '#ef4444' : '#10b981'};
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            z-index: 10001;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        `;
        messageDiv.textContent = message;
        
        document.body.appendChild(messageDiv);
        
        setTimeout(() => {
            if (messageDiv.parentNode) {
                messageDiv.parentNode.removeChild(messageDiv);
            }
        }, duration);
    }

    removeExistingOverlay() {
        const existing = document.getElementById(this.overlayId);
        if (existing) {
            existing.remove();
        }
    }

    destroy() {
        this.removeExistingOverlay();
        super.destroy();
    }
}